import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Modal } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useRfiCategory from 'hooks/useRfiCategory';
import useRfiReassignCategories from 'router/subrouters/DataRoom/hooks/useRfiReassignCategories';
import { DataRoomRFICategory } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { getArrayLength, isSingleElementArray } from 'utils';
import RfiReassignForm from './components/RfiReassignForm';

import './RfiReassignCategoriesModal.styles.scss';

const MAX_VISIBLE_DOCUMENT_NAMES = 5;
const TRUNCATED_DOCUMENT_NAMES_COUNT = 3;

type RfiReassignCategoriesModalProps = {
  className?: string;
  categories: DataRoomRFICategory[];
  assignedEmails: string[];
  onCategoryUpdate: (category: DataRoomRFICategory) => void;
  onReassigned?: () => void;
};

const RfiReassignCategoriesModal: ForwardRefRenderFunction<
  ModalRef,
  RfiReassignCategoriesModalProps
> = (props, ref) => {
  const {
    className,
    categories,
    assignedEmails,
    onCategoryUpdate,
    onReassigned,
  } = props;

  const classes = classNames('rfi-reassign-categories-modal', className);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();
  const { getCategoryName } = useRfiCategory();

  const onSent = useCallback(() => {
    modal?.close();
    onReassigned?.();
  }, [modal, onReassigned]);

  const { reassignCategories, loading } = useRfiReassignCategories(
    categories,
    onCategoryUpdate,
    onSent,
  );

  const count = getArrayLength(categories);

  const distinctAssignees = useMemo(
    () =>
      Array.from(
        new Set(categories.map(({ assignedTo }) => assignedTo).filter(Boolean)),
      ),
    [categories],
  );

  const isSingleAssignee = isSingleElementArray(distinctAssignees);

  const names = useMemo(
    () => categories.map(({ name }) => getCategoryName(name)),
    [categories, getCategoryName],
  );

  const visibleNames = useMemo(
    () =>
      names.length > MAX_VISIBLE_DOCUMENT_NAMES
        ? names.slice(0, TRUNCATED_DOCUMENT_NAMES_COUNT)
        : names,
    [names],
  );

  const extraNamesCount = useMemo(
    () =>
      names.length > MAX_VISIBLE_DOCUMENT_NAMES
        ? names.length - TRUNCATED_DOCUMENT_NAMES_COUNT
        : 0,
    [names],
  );

  const onSubmit = useCallback(
    async ({ assignedTo }) => await reassignCategories(assignedTo),
    [reassignCategories],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('reassignDocumentsCount', { count })}
    >
      <div className="rfi-reassign-categories-modal__moving-from">
        <p className="rfi-reassign-categories-modal__label">
          {t('reassignMovingFromLabel')}
        </p>

        <p className="rfi-reassign-categories-modal__muted-text">
          {isSingleAssignee
            ? distinctAssignees[0]
            : t('reassignMovingFromMultiplePeople', {
                count: getArrayLength(distinctAssignees),
              })}
        </p>
      </div>

      <p className="rfi-reassign-categories-modal__label">{t('documents')}</p>

      <div className="rfi-reassign-categories-modal__documents">
        {visibleNames.map((name, index) => (
          <p key={`${name}-${index}`}>{name}</p>
        ))}

        {!!extraNamesCount && (
          <p className="rfi-reassign-categories-modal__muted-text">
            {t('reassignMoreDocuments', { count: extraNamesCount })}
          </p>
        )}
      </div>

      <RfiReassignForm
        assignedEmails={assignedEmails}
        isSingleAssignee={isSingleAssignee}
        distinctAssignees={distinctAssignees}
        loading={loading}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default forwardRef(RfiReassignCategoriesModal);
